<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import format from 'date-fns/format';

import Spinner from 'shared/components/Spinner.vue';
import InstagramMediaReportsAPI from 'dashboard/api/instagramMediaReports';

const props = defineProps({
  selectedInbox: {
    type: Object,
    default: null,
  },
});

// Data state
const posts = ref([]);
const totalCount = ref(0);
const isLoading = ref(false);
const currentPage = ref(1);
const perPage = ref(25);
const classificationFilter = ref('');
const searchQuery = ref('');

// Computed
const filteredPosts = computed(() => {
  if (!searchQuery.value.trim()) return posts.value;
  const q = searchQuery.value.toLowerCase();
  return posts.value.filter(
    p =>
      (p.username && p.username.toLowerCase().includes(q)) ||
      (p.caption && p.caption.toLowerCase().includes(q)) ||
      (p.profile_name && p.profile_name.toLowerCase().includes(q))
  );
});

const totalPages = computed(() => {
  return Math.max(1, Math.ceil(totalCount.value / perPage.value));
});

const showingFrom = computed(() => {
  return (currentPage.value - 1) * perPage.value + 1;
});

const showingTo = computed(() => {
  return Math.min(currentPage.value * perPage.value, totalCount.value);
});

// Helper functions
function classificationClass(classification) {
  if (classification === 'alerta_adversario') return 'bg-n-ruby-9';
  if (classification === 'pro_adversario') return 'bg-n-orange-9';
  if (classification === 'pro_candidato') return 'bg-n-teal-9';
  return 'bg-n-slate-9';
}

function classificationLabel(classification) {
  if (classification === 'alerta_adversario') return 'Alerta de adversario';
  if (classification === 'pro_adversario') return 'Pro-adversario';
  if (classification === 'pro_candidato') return 'Pro-candidato';
  return 'Pessoal/Ignorado';
}

function riskClass(risk) {
  if (risk === 'high' || risk === 'critical') return 'bg-n-ruby-9';
  if (risk === 'medium') return 'bg-n-amber-9';
  return 'bg-n-teal-9';
}

function riskLabel(risk) {
  if (risk === 'high' || risk === 'critical') return 'Alto';
  if (risk === 'medium') return 'Medio';
  return 'Baixo';
}

function truncateText(text, maxLength) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

function formatDate(dateStr) {
  try {
    return format(new Date(dateStr), 'dd/MM/yyyy HH:mm');
  } catch {
    return dateStr;
  }
}

// API functions
async function fetchPosts() {
  if (!props.selectedInbox) return;
  isLoading.value = true;
  try {
    const response = await InstagramMediaReportsAPI.getAuditedPosts({
      inboxId: props.selectedInbox.id,
      classification: classificationFilter.value || undefined,
      page: currentPage.value,
    });
    posts.value = response.data.posts || [];
    totalCount.value = response.data.total_count || 0;
  } catch {
    posts.value = [];
    totalCount.value = 0;
  } finally {
    isLoading.value = false;
  }
}

function goToPage(page) {
  if (page < 1 || page > totalPages.value) return;
  currentPage.value = page;
  fetchPosts();
}

function onClassificationChange() {
  currentPage.value = 1;
  fetchPosts();
}

function loadData() {
  if (props.selectedInbox) {
    currentPage.value = 1;
    fetchPosts();
  }
}

watch(() => props.selectedInbox, loadData);

onMounted(loadData);
</script>

<template>
  <div>
    <div
      v-if="!selectedInbox"
      class="flex items-center justify-center py-12 text-n-slate-11"
    >
      {{ $t('INSTAGRAM_MEDIA_REPORTS.TRACKER_SELECT_INBOX') }}
    </div>

    <template v-else>
      <div
        v-if="isLoading"
        class="flex items-center justify-center py-12"
      >
        <Spinner />
      </div>

      <template v-else>
        <!-- Filter row -->
        <div class="flex items-center gap-3 mb-4 flex-wrap">
          <select
            v-model="classificationFilter"
            class="py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12"
            @change="onClassificationChange"
          >
            <option value="">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_FILTER_ALL') }}
            </option>
            <option value="alerta_adversario">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_FILTER_ALERTA') }}
            </option>
            <option value="pro_adversario">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_FILTER_PRO_ADV') }}
            </option>
            <option value="pro_candidato">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_FILTER_PRO_CAND') }}
            </option>
            <option value="pessoal_ignorado">
              {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_FILTER_PESSOAL') }}
            </option>
          </select>
          <input
            v-model="searchQuery"
            type="text"
            class="py-1.5 px-3 text-sm rounded-lg border bg-n-solid-2 border-n-weak text-n-slate-12 w-64"
            :placeholder="$t('INSTAGRAM_MEDIA_REPORTS.POSTS_SEARCH_PLACEHOLDER')"
          />
          <div class="ml-auto flex items-center gap-2">
            <button
              class="px-3 py-1.5 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:border-n-slate-7 transition-colors disabled:opacity-50"
              :disabled="currentPage <= 1"
              @click="goToPage(currentPage - 1)"
            >
              &laquo;
            </button>
            <span class="text-xs text-n-slate-11">
              {{ currentPage }} / {{ totalPages }}
            </span>
            <button
              class="px-3 py-1.5 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:border-n-slate-7 transition-colors disabled:opacity-50"
              :disabled="currentPage >= totalPages"
              @click="goToPage(currentPage + 1)"
            >
              &raquo;
            </button>
          </div>
        </div>

        <!-- Table -->
        <div class="bg-n-solid-2 rounded-lg border border-n-weak mb-6">
          <div v-if="filteredPosts.length" class="overflow-x-auto">
            <table class="w-full text-sm">
              <thead>
                <tr class="border-b border-n-weak">
                  <th class="text-left py-2 px-3 text-xs font-medium text-n-slate-11">#</th>
                  <th class="text-left py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_COL_LEADERSHIP') }}
                  </th>
                  <th class="text-left py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_COL_USER_URL') }}
                  </th>
                  <th class="text-left py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_COL_TEXT') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_COL_CLASSIFICATION') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_COL_POST_TYPE') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_COL_MEDIA') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_COL_LIKES') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_COL_COMMENTS') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_COL_RISK') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_COL_PUBLISHED') }}
                  </th>
                  <th class="text-center py-2 px-3 text-xs font-medium text-n-slate-11">
                    {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_COL_ACTIONS') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(post, index) in filteredPosts"
                  :key="post.id"
                  class="border-b border-n-weak"
                >
                  <td class="py-2 px-3 text-n-slate-11 text-xs">
                    {{ (currentPage - 1) * perPage + index + 1 }}
                  </td>
                  <td class="py-2 px-3 text-n-slate-12 font-medium text-xs">
                    {{ post.profile_name || '-' }}
                  </td>
                  <td class="py-2 px-3 text-n-blue-11 text-xs">
                    @{{ post.username || '-' }}
                  </td>
                  <td class="py-2 px-3 text-n-slate-11 text-xs max-w-xs">
                    {{ truncateText(post.caption, 100) }}
                  </td>
                  <td class="py-2 px-3 text-center">
                    <span
                      class="text-xs px-2 py-0.5 rounded-full text-white"
                      :class="classificationClass(post.classification)"
                    >
                      {{ classificationLabel(post.classification) }}
                    </span>
                  </td>
                  <td class="py-2 px-3 text-center text-n-slate-11 text-xs">
                    {{ post.post_type || '-' }}
                  </td>
                  <td class="py-2 px-3 text-center text-n-slate-11 text-xs">
                    {{ post.media_type || '-' }}
                  </td>
                  <td class="py-2 px-3 text-center text-n-slate-11 text-xs">
                    &#x2764; {{ post.likes_count || 0 }}
                  </td>
                  <td class="py-2 px-3 text-center text-n-slate-11 text-xs">
                    &#x1F4AC; {{ post.comments_count || 0 }}
                  </td>
                  <td class="py-2 px-3 text-center">
                    <span
                      class="text-xs px-2 py-0.5 rounded-full text-white"
                      :class="riskClass(post.risk_level)"
                    >
                      {{ riskLabel(post.risk_level) }}
                    </span>
                  </td>
                  <td class="py-2 px-3 text-center text-n-slate-11 text-xs">
                    {{ post.published_at ? formatDate(post.published_at) : '-' }}
                  </td>
                  <td class="py-2 px-3 text-center">
                    <div class="flex items-center justify-center gap-1">
                      <a
                        v-if="post.permalink"
                        :href="post.permalink"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="px-2 py-1 text-xs rounded-lg border border-n-weak text-n-blue-11 hover:border-n-blue-7 transition-colors"
                      >
                        {{ $t('INSTAGRAM_MEDIA_REPORTS.OPEN_LINK') }}
                      </a>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="flex items-center justify-center py-8 text-n-slate-11">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_NO_DATA') }}
          </div>
        </div>

        <!-- Bottom pagination info -->
        <div v-if="filteredPosts.length" class="flex items-center justify-between">
          <span class="text-xs text-n-slate-11">
            {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_SHOWING') }} {{ showingFrom }} {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_SHOWING_TO') }} {{ showingTo }} {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_SHOWING_OF') }} {{ totalCount }} {{ $t('INSTAGRAM_MEDIA_REPORTS.POSTS_SHOWING_RECORDS') }}
          </span>
          <div class="flex items-center gap-2">
            <button
              class="px-3 py-1.5 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:border-n-slate-7 transition-colors disabled:opacity-50"
              :disabled="currentPage <= 1"
              @click="goToPage(currentPage - 1)"
            >
              &laquo;
            </button>
            <span class="text-xs text-n-slate-11">
              {{ currentPage }} / {{ totalPages }}
            </span>
            <button
              class="px-3 py-1.5 text-xs rounded-lg border border-n-weak text-n-slate-11 hover:border-n-slate-7 transition-colors disabled:opacity-50"
              :disabled="currentPage >= totalPages"
              @click="goToPage(currentPage + 1)"
            >
              &raquo;
            </button>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>
